import { useEffect, useMemo, useState } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, limit, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';

const PAGE_SIZE = 30;
const EMPTY_RATES = () => ({ weekdays: 0, saturday: 0, sunday: 0 });
const normalizeRates = (rates = {}) => ({
  weekdays: Number(rates.weekdays || 0),
  saturday: Number(rates.saturday || 0),
  sunday: Number(rates.sunday || 0),
});

export default function EmployeeManagementView({ onBack, currentUser }) {
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [selectedEmployeeReceipts, setSelectedEmployeeReceipts] = useState(null);
  const [confirmDel, setConfirmDel] = useState({ open: false, employee: null, loading: false, error: '' });
  const [page, setPage] = useState(1);
  const [globalDailyRates, setGlobalDailyRates] = useState(EMPTY_RATES());

    // Carga acotada de empleados (sin stream masivo)
  useEffect(() => {
    let cancelled = false;
    const loadEmployees = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'employees'), limit(PAGE_SIZE * 2)));
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEmployees(rows);
      } catch (err) {
        console.error('No se pudieron cargar empleados', err);
      }
    };
    loadEmployees();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadDefaults = async () => {
      try {
        const ref = doc(db, 'payrollSettings', 'global');
        const snap = await getDoc(ref);
        if (!cancelled && snap.exists()) {
          const rates = snap.data()?.dailyDefaultRates || {};
          setGlobalDailyRates({
            weekdays: Number(rates.weekdays || 0),
            saturday: Number(rates.saturday || 0),
            sunday: Number(rates.sunday || 0),
          });
        }
      } catch (err) {
        console.error('No se pudo cargar configuración de nómina', err);
      }
    };
    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, []);


  // Paginación
  const totalPages = Math.max(1, Math.ceil(employees.length / PAGE_SIZE));
  const pagedEmployees = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return employees.slice(start, start + PAGE_SIZE);
  }, [employees, page]);
  useEffect(() => { setPage(1); }, [employees.length]);

  useEffect(() => {
    if (!showForm || !editingEmployee?.useGlobalRates) return;
    setEditingEmployee((prev) => ({
      ...(prev || {}),
      dailyRates: getDefaultRates(),
    }));
  }, [globalDailyRates, showForm]);

  const getDefaultRates = () => normalizeRates(globalDailyRates);

  const handleSaveEmployee = async (employeeData) => {
    const normalizedRates = employeeData?.dailyRates || EMPTY_RATES();
    const payload = {
      name: employeeData.name || '',
      lastName: employeeData.lastName || '',
      identification: employeeData.identification || '',
      position: employeeData.position || '',
      baseSalary: Number(employeeData.baseSalary || 0),
      hireDate: employeeData.hireDate || '',
      contractType: employeeData.contractType || 'Indefinido',
      email: employeeData.email || '',
      status: employeeData.status || 'activo',
      dailyRates: {
        weekdays: Number(normalizedRates.weekdays || 0),
        saturday: Number(normalizedRates.saturday || 0),
        sunday: Number(normalizedRates.sunday || 0),
      },
      useGlobalRates: !!employeeData.useGlobalRates,
    };
    try {
      if (employeeData?.id) {
        await updateDoc(doc(db, 'employees', employeeData.id), payload);
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.id === employeeData.id ? { ...emp, ...payload } : emp
          )
        );
      } else {
        const ref = await addDoc(collection(db, 'employees'), payload);
        setEmployees((prev) => [{ id: ref.id, ...payload }, ...prev]);
      }
      setEditingEmployee(null);
      setShowForm(false);
    } catch (e) {
      console.error(e);
      alert('No se pudo guardar el empleado');
    }
  };

  const openDeleteModal = (employee) => setConfirmDel({ open: true, employee, loading: false, error: '' });
  const closeDeleteModal = () => setConfirmDel({ open: false, employee: null, loading: false, error: '' });
  const confirmDelete = async () => {
    if (!confirmDel.employee?.id) return;
    setConfirmDel((s) => ({ ...s, loading: true, error: '' }));
    try {
      await deleteDoc(doc(db, 'employees', confirmDel.employee.id));
      setEmployees((prev) => prev.filter((emp) => emp.id !== confirmDel.employee.id));
      setConfirmDel({ open: false, employee: null, loading: false, error: '' });
    } catch (e) {
      console.error(e);
      setConfirmDel((s) => ({ ...s, loading: false, error: 'No se pudo eliminar. Intenta de nuevo.' }));
    }
  };

  const handleEditEmployee = (employee) => {
    const normalized = {
      ...employee,
      dailyRates: {
        weekdays: Number(employee?.dailyRates?.weekdays ?? 0),
        saturday: Number(employee?.dailyRates?.saturday ?? 0),
        sunday: Number(employee?.dailyRates?.sunday ?? 0),
      },
      useGlobalRates: employee?.useGlobalRates ?? false,
    };
    if (normalized.useGlobalRates) {
      normalized.dailyRates = getDefaultRates();
    }
    setEditingEmployee(normalized);
    setShowForm(true);
  };

  const handleViewReceipts = (employee) => {
    setSelectedEmployeeReceipts(employee);
  };

  return (
    <div className="p-4 sm:p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center mb-6 sm:mb-8">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 transition duration-200 px-4 py-2 rounded-lg border border-blue-200 hover:border-blue-400 w-full md:w-auto justify-center md:justify-start"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <h1 className="text-2xl sm:text-4xl font-extrabold text-gray-900 drop-shadow-sm text-center md:text-left">Gestión de Empleados</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingEmployee({
              name: '',
              lastName: '',
              identification: '',
              position: '',
              baseSalary: 0,
              hireDate: '',
              contractType: 'Indefinido',
              email: '',
              dailyRates: getDefaultRates(),
              useGlobalRates: true,
            });
          }}
          className="px-4 sm:px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 shadow-md hover:shadow-lg flex items-center w-full md:w-auto justify-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nuevo Empleado
        </button>
      </div>

      {showForm ? (
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 w-full max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
            {editingEmployee && editingEmployee.id ? 'Editar Empleado' : 'Registrar Nuevo Empleado'}
          </h2>
          <form onSubmit={(e) => { e.preventDefault(); handleSaveEmployee(editingEmployee || {}); }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input
                  type="text"
                  value={editingEmployee?.name || ''}
                  onChange={(e) => setEditingEmployee((prev) => ({ ...(prev || {}), name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apellido</label>
                <input
                  type="text"
                  value={editingEmployee?.lastName || ''}
                  onChange={(e) => setEditingEmployee((prev) => ({ ...(prev || {}), lastName: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Identificación</label>
                <input
                  type="text"
                  value={editingEmployee?.identification || ''}
                  onChange={(e) => setEditingEmployee((prev) => ({ ...(prev || {}), identification: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
                <input
                  type="text"
                  value={editingEmployee?.position || ''}
                  onChange={(e) => setEditingEmployee((prev) => ({ ...(prev || {}), position: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salario Base</label>
                <input
                  type="number"
                  value={editingEmployee?.baseSalary ?? ''}
                  onChange={(e) =>
                    setEditingEmployee((prev) => ({
                      ...(prev || {}),
                      baseSalary: e.target.value === '' ? '' : Number(e.target.value),
                    }))
                  }
                  min="0"
                  step="1000"
                  placeholder="Ingrese salario sin auxilio"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Ingreso</label>
                <input
                  type="date"
                  value={editingEmployee?.hireDate || ''}
                  onChange={(e) => setEditingEmployee((prev) => ({ ...(prev || {}), hireDate: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Contrato</label>
                <select
                  value={editingEmployee?.contractType || 'Indefinido'}
                  onChange={(e) => setEditingEmployee((prev) => ({ ...(prev || {}), contractType: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Seleccionar...</option>
                  <option value="Indefinido">Indefinido</option>
                  <option value="Fijo">Fijo</option>
                  <option value="Por Obra">Por Obra</option>
                </select>
              </div>
              <div className="md:col-span-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <p className="text-sm font-semibold text-gray-700">Tarifas Diarias</p>
                  <label className="inline-flex items-center text-sm text-gray-600">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={!!editingEmployee?.useGlobalRates}
                      onChange={(e) =>
                        setEditingEmployee((prev) => ({
                          ...(prev || {}),
                          useGlobalRates: e.target.checked,
                          dailyRates: e.target.checked
                            ? getDefaultRates()
                            : prev?.dailyRates || EMPTY_RATES(),
                        }))
                      }
                    />
                    Usar tarifas por defecto
                  </label>
                </div>
                {editingEmployee?.useGlobalRates && (
                  <p className="text-xs text-gray-500 mb-3">
                    Valores tomados de la configuración de nómina. Desactiva el interruptor para personalizar.
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Lunes a viernes</label>
                    <input
                      type="number"
                      min="0"
                      value={editingEmployee?.dailyRates?.weekdays ?? 0}
                      onChange={(e) =>
                        setEditingEmployee((prev) => ({
                          ...(prev || {}),
                          dailyRates: {
                            ...(prev?.dailyRates || EMPTY_RATES()),
                            weekdays: e.target.value === '' ? '' : Number(e.target.value),
                          },
                        }))
                      }
                      disabled={!!editingEmployee?.useGlobalRates}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        editingEmployee?.useGlobalRates ? 'bg-gray-100 border-gray-200 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Sábado</label>
                    <input
                      type="number"
                      min="0"
                      value={editingEmployee?.dailyRates?.saturday ?? 0}
                      onChange={(e) =>
                        setEditingEmployee((prev) => ({
                          ...(prev || {}),
                          dailyRates: {
                            ...(prev?.dailyRates || EMPTY_RATES()),
                            saturday: e.target.value === '' ? '' : Number(e.target.value),
                          },
                        }))
                      }
                      disabled={!!editingEmployee?.useGlobalRates}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        editingEmployee?.useGlobalRates ? 'bg-gray-100 border-gray-200 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Domingo / Festivo</label>
                    <input
                      type="number"
                      min="0"
                      value={editingEmployee?.dailyRates?.sunday ?? 0}
                      onChange={(e) =>
                        setEditingEmployee((prev) => ({
                          ...(prev || {}),
                          dailyRates: {
                            ...(prev?.dailyRates || EMPTY_RATES()),
                            sunday: e.target.value === '' ? '' : Number(e.target.value),
                          },
                        }))
                      }
                      disabled={!!editingEmployee?.useGlobalRates}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        editingEmployee?.useGlobalRates ? 'bg-gray-100 border-gray-200 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Estas tarifas se usan para calcular pagos diarios basados en asistencia.
                </p>
              </div>
            </div>
            <div className="mt-8 flex justify-end space-x-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300">
                Cancelar
              </button>
              <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                {editingEmployee && editingEmployee.id ? 'Guardar Cambios' : 'Registrar Empleado'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Identificación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cargo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Base</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Ingreso</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo Contrato</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedEmployees.length > 0 ? (
                  pagedEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50 transition duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{employee.name} {employee.lastName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.identification || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.position}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${Number(employee.baseSalary || 0).toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.hireDate || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.contractType || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => handleEditEmployee(employee)} className="text-blue-600 hover:text-blue-800 transition duration-150 mr-3">
                          Editar
                        </button>
                        <button onClick={() => openDeleteModal(employee)} className="text-red-600 hover:text-red-800 transition duration-150 mr-3">
                          Eliminar
                        </button>
                        <button onClick={() => handleViewReceipts(employee)} className="text-purple-600 hover:text-purple-800 transition duration-150">
                          Ver Comprobantes
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                      No hay empleados registrados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-4 text-sm text-gray-600">
            <div>
              Mostrando {employees.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, employees.length)} de {employees.length}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPage(1)} disabled={page === 1} className="px-3 py-1 rounded border disabled:opacity-50">
                Primero
              </button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 rounded border disabled:opacity-50">
                Anterior
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 rounded border disabled:opacity-50">
                Siguiente
              </button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-3 py-1 rounded border disabled:opacity-50">
                Último
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Eliminar empleado</h3>
            <p className="text-sm text-gray-600 mb-4">
              ¿Estás seguro de eliminar a <span className="font-semibold">{confirmDel.employee?.name} {confirmDel.employee?.lastName}</span>? Esta acción no se puede deshacer.
            </p>
            {confirmDel.error && <div className="mb-3 text-sm text-red-600">{confirmDel.error}</div>}
            <div className="flex justify-end space-x-3 mt-4">
              <button onClick={closeDeleteModal} disabled={confirmDel.loading} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300">
                Cancelar
              </button>
              <button onClick={confirmDelete} disabled={confirmDel.loading} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {confirmDel.loading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedEmployeeReceipts && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Comprobantes de {selectedEmployeeReceipts.name}</h2>
            {selectedEmployeeReceipts.payrollReceipts?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Periodo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salario Neto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Generado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedEmployeeReceipts.payrollReceipts.map((receipt) => (
                      <tr key={receipt.id} className="hover:bg-gray-50 transition duration-150">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{receipt.period}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${receipt.netSalary.toFixed(2)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{receipt.dateGenerated}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button onClick={() => alert(`Descargando comprobante ${receipt.id}`)} className="text-blue-600 hover:text-blue-800 transition duration-150">
                            Descargar PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500">No hay comprobantes de nómina generados para este empleado.</p>
            )}
            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setSelectedEmployeeReceipts(null)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

